import { useEffect, useState } from 'react'
import { apiService, type TenantEntitlementLayer } from '@/service/api'
import { useAppStore } from '@/store'

export interface TenantEntitlements {
  /** Layers the active tenant owns; empty while loading or unknown. */
  layers: TenantEntitlementLayer[]
  loading: boolean
  /** `false` is the only value that hides graph views. `null` = not known (loading or the request failed). */
  hasUkg: boolean | null
  hasRetina: boolean | null
}

/** One request per tenant per page load, shared by every component that asks. */
const cache = new Map<string, Promise<TenantEntitlementLayer[] | null>>()

function fetchLayers(tenantKey: string): Promise<TenantEntitlementLayer[] | null> {
  let pending = cache.get(tenantKey)
  if (!pending) {
    pending = apiService
      .getTenantEntitlements()
      .then((r) => r.layers)
      // Fail open: an unknown entitlement must never hide the graph from a tenant that owns it.
      .catch(() => null)
    cache.set(tenantKey, pending)
  }
  return pending
}

/** @internal Test seam. */
export function __resetTenantEntitlementsCache(): void {
  cache.clear()
}

/**
 * The active tenant's product layers (Opening Cortex). A tenant that owns Retina but not
 * UKG is "Retina-only": documents work, graph views show a designed empty state.
 */
export function useTenantEntitlements(): TenantEntitlements {
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const [state, setState] = useState<{ layers: TenantEntitlementLayer[] | null; loading: boolean }>({
    layers: null,
    loading: true,
  })

  useEffect(() => {
    let mounted = true
    setState({ layers: null, loading: true })
    fetchLayers(activeOrganizationId ?? '__no_tenant__').then((layers) => {
      if (mounted) setState({ layers, loading: false })
    })
    return () => {
      mounted = false
    }
  }, [activeOrganizationId])

  const has = (layer: TenantEntitlementLayer): boolean | null =>
    state.layers === null ? null : state.layers.includes(layer)

  return { layers: state.layers ?? [], loading: state.loading, hasUkg: has('ukg'), hasRetina: has('retina') }
}
